import {
  useHPTARGeneralMessage,
  useHPTARInvalids,
  useHPTARs,
  useInfiniteKmzInvalids,
} from "@/api/hptar";
import { useGenerateHPTARXlsx, useRFS } from "@/api/rfs";
import { MISSING_ID_IN_ROUTE, STATUS } from "@/utils/constant";
import { swalErrorDefault } from "@/utils/lib/swal";
import {
  Checking,
  ContainerPrimary,
  ContainerSecondary,
  Unvalidated,
  Validated,
} from "../ui";
import { DownloadButton } from "@/components/ui/button";
import { Anchor } from "@/components/ui/anchor";
import { RequiredAsterisk } from "@/components/form/required-asterisk";
import { isHPTARValidated } from "../utils/validation";

export function HPTARHandler({
  id,
  hasRequiredAsterisk = false,
}: {
  id: string;
  hasRequiredAsterisk?: boolean;
}) {
  const { data: rfs } = useRFS({ id });
  const { data: hptars, isPending: isLoadingHptars } = useHPTARs({ rfsId: id });
  const { data: hptarInvalids, isPending: isLoadingHptarInvalids } =
    useHPTARInvalids({ rfsId: id });
  const { data: generalMessages, isPending: isLoadingGeneralMessages } =
    useHPTARGeneralMessage({
      rfsId: id,
    });
  const { data: kmzInvalids, isPending: isLoadingKmzInvalids } =
    useInfiniteKmzInvalids({ rfsId: id });

  const { mutate: generateHPTAR, isPending: isDownloading } =
    useGenerateHPTARXlsx();

  const isValidated = isHPTARValidated({
    hptars,
    hptarInvalids,
    hptarGeneralMessages: generalMessages,
    kmzInvalids: kmzInvalids?.pages?.[0]?.content,
  });

  const statusEnableCreate = [
    STATUS.RFS.PREPARATION_DATA,
    STATUS.RFS.REJECTED_DOCUMENT_RFS,
    STATUS.RFS.REVISION_BY_LN,
  ];
  const enableCreate =
    statusEnableCreate.includes(rfs?.statusRfsSubmission ?? "") ||
    (!isValidated &&
      rfs?.status === STATUS.RFS_SUBMISSIONS_VALUES.PROCESSING_HPM);

  function handleDownload() {
    if (!id) {
      throw new Error(MISSING_ID_IN_ROUTE);
    }
    generateHPTAR(
      { id, name: `HPTAR-${rfs?.clusterId}.xlsx` },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  }

  return (
    <ContainerPrimary>
      <span className="flex-grow whitespace-nowrap">
        HPTAR (xlsx){hasRequiredAsterisk && <RequiredAsterisk />}
      </span>
      <ContainerSecondary>
        {isLoadingHptars ||
        isLoadingHptarInvalids ||
        isLoadingGeneralMessages ||
        isLoadingKmzInvalids ? (
          <Checking />
        ) : (
          <>
            {isValidated ? <Validated /> : <Unvalidated />}
            {isValidated && (
              <DownloadButton
                onClick={handleDownload}
                loading={isDownloading}
              />
            )}
            {enableCreate ? (
              <Anchor
                mood="axiata-blue-outline"
                href={`/rfs/rfs/${id}/hptar/create`}
                state={{ from: `/rfs/rfs/${id}?tab=rfs-process` }}
              >
                {!isValidated ? <p>Create</p> : <p>Detail</p>}
              </Anchor>
            ) : (
              <p className="flex h-9 w-fit cursor-not-allowed items-center justify-center rounded-md border border-[#c4c4c480] bg-neutral-200 px-3 text-sm opacity-50 dark:text-neutral-300">
                Create
              </p>
            )}
          </>
        )}
      </ContainerSecondary>
    </ContainerPrimary>
  );
}
